import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Plug, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { toast } from '@/hooks/useToast';
import { connectionReach } from '@/lib/oauth-connections';
import { oauthConnectionsQueryOptions, useRevokeOauthConnectionMutation } from '@/lib/query-options';
import type { OauthConnection } from '@/lib/types';

/** Apps connected through OAuth, such as Claude, and the way to disconnect one (#6612). */
export const ConnectedAppsSettings = () => {
  const [revokeTarget, setRevokeTarget] = useState<OauthConnection | null>(null);
  const { data, isLoading } = useQuery(oauthConnectionsQueryOptions());
  const revoke = useRevokeOauthConnectionMutation();

  const connections = data?.connections ?? [];

  const confirmRevoke = () => {
    if (!revokeTarget) return;
    revoke.mutate(revokeTarget.id, {
      onSuccess: () => setRevokeTarget(null),
      onError: (error: Error) =>
        toast({ title: 'Revocation failed', description: error.message, variant: 'destructive' }),
    });
  };

  return (
    <div className="mt-8">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Connected apps</h2>
        <p className="text-sm text-gray-500">
          Apps such as Claude that were given access to act for you. Revoking one disconnects it immediately.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading connected apps…</p>}
      {!isLoading && connections.length === 0 && (
        <EmptyState
          icon={<Plug className="h-8 w-8 text-gray-400" />}
          title="No connected apps"
          description="An app appears here once access is approved for it."
        />
      )}

      {connections.length > 0 && (
        <div className="divide-y rounded-lg border bg-white shadow-sm">
          {connections.map((connection) => (
            <div key={connection.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="font-medium text-gray-900">{connection.client_name}</div>
                <div className="text-sm text-gray-500">
                  {connectionReach(connection.surfaces)} · Last active{' '}
                  {formatDistanceToNow(new Date(connection.last_used_at), { addSuffix: true })}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {connection.scopes.map((scope) => (
                    <span key={scope} className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-800">
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-gray-400">
                  Connected {new Date(connection.connected_at).toLocaleDateString()}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-700 hover:bg-red-50 hover:text-red-800"
                  onClick={() => setRevokeTarget(connection)}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  Revoke
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConformationModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
        isLoading={revoke.isPending}
        title={`Revoke ${revokeTarget?.client_name ?? 'this app'}?`}
        description="The app loses access immediately and has to be connected again to act for you."
        confirmText="Revoke access"
      />
    </div>
  );
};
